import { useState, useEffect } from "react";
import { IContainerItem, IContainerItemListResponse, mapToContainerItem } from "../../common/ContainerSchemas";
import { ItemsApiProvider } from "../../providers/ItemsApiProvider";
import { Search20Filled, Dismiss20Filled } from "@fluentui/react-icons";
import { Input, Skeleton, SkeletonItem, Spinner } from "@fluentui/react-components";
import { IContainerSearchItemsProps } from "../types/IContainerSearchItemsProps";
import "./ContainerSearchItems.css";
import { useContainerSearchItemsStyles } from "./ContainerSearchItems.styles";

export const ContainerSearchItems: React.FunctionComponent<IContainerSearchItemsProps> = (props: IContainerSearchItemsProps) => {
	const searchStyles = useContainerSearchItemsStyles();
	const [error, setError] = useState<Error | null>(null);
	const [searchQuery, setSearchQuery] = useState<string>("");
	const [lastSearchedQuery, setLastSearchedQuery] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const itemsApi = ItemsApiProvider.instance;

	const fetchSearchResults = async () => {
		if (!props.searchQuery.trim()) {
			props.onSearchResults([]);
			props.setTotalResults(0);
			props.setIsLoading?.(false); 
			setLastSearchedQuery("");
			return;
		}
		setIsLoading(true);
		props.setIsLoading?.(true);
		try {
			let response: IContainerItemListResponse;
			if(props.containerId.toLowerCase() === "all"){
				response = await itemsApi.search(props.searchQuery, props.page, props.pageSize);
			} else {
				response = await itemsApi.search(props.searchQuery, props.page, props.pageSize, props.containerId);
			}

			const results: IContainerItem[] = response.items.map(mapToContainerItem);

			props.onSearchResults(results);
			props.setTotalResults(response.total_count || 0);
			setLastSearchedQuery(props.searchQuery);

		} catch (err) {
			console.error("Error fetching search results:", err);
			setError(new Error("Failed to fetch search results. Please try again."));
			props.onSearchResults([]);
			props.setTotalResults(0);
		} finally {
			setIsLoading(false); 
			props.setIsLoading?.(false);
		}
	};

	const clearSearch = () => {
		setSearchQuery("");
		props.onSearchResults([]);
		props.setTotalResults(0);
		props.onSearchQueryChange("");
		setLastSearchedQuery("");
	};

	if (error) {
		throw error;
	}

	// Sync local searchQuery state with props.searchQuery
	useEffect(() => {
		setSearchQuery(props.searchQuery);
	}, [props.searchQuery]);

	useEffect(() => {
        if (props.searchQuery.trim()) {
            fetchSearchResults();
        }
    }, [props.searchQuery, props.page, props.pageSize]);

	if (props.isUILoading) {
    return (
      <>
        {props.isContainerRoot && (
          <div className="searchContainer">
            <Skeleton className={searchStyles.skeleton}>
              <SkeletonItem className={searchStyles.skeletonItem} />
            </Skeleton>
          </div>
        )}
      </>
    );
  }

	return (
		<>
			{props.isContainerRoot ? (
				<div className="searchContainer">
					<Search20Filled className="searchIcon" />
					<Input
						type="text"
						placeholder="Search in storage"
						value={searchQuery}
						onChange={(e) => {
							const query = e.target.value;
							setSearchQuery(query);

							if (query.trim().length === 0) {
								props.onSearchResults([]);
								props.setTotalResults(0);
								props.onSearchQueryChange("");
							}
						}}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								props.setPage(1); // Reset to first page for new search
								props.onSearchQueryChange(searchQuery);
								// fetchSearchResults will be triggered by useEffect when searchQuery/page changes
							}
						}}
						onBlur={() => {
							if (searchQuery.trim() && searchQuery !== lastSearchedQuery) {
								props.setPage(1); // Reset to first page for new search
								props.onSearchQueryChange(searchQuery);
								// fetchSearchResults will be triggered by useEffect when searchQuery/page changes
							}
						}}
						className="searchInput"
					/>
					{searchQuery && (
						<Dismiss20Filled 
							className="clearIcon" 
							onClick={clearSearch}
							title="Clear search"
						/>
					)}
				</div>
			) : <></>}
			{isLoading && (
				<div className="overlay">
					<Spinner size="large" label="Loading..." />
				</div>
			)}
		</>
	);
}
